import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AUTH_TOKEN_KEY,
  fetchAdminTeamInfo,
  fetchSystemUserInfo,
  formatInvoiceTaxLabel,
  getUserTeams,
  isOwnCreatedUserTeam,
  postApplyExitTeam,
  showApiResultModal,
  type SystemUserInfo,
  type UserTeamMembership,
} from '@/api';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import { TEAM_COUNTRY_OPTIONS, normalizeTeamCountry } from '@/constants/teamCountries';

function initialsFromName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return 'T';
  return trimmed
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function formatTeamDateTime(value?: string | null): string {
  if (!value || !value.trim()) return '—';
  return value.trim().replace('T', ' ').slice(0, 16);
}

function countryLabel(code?: string): string {
  const raw = code?.trim();
  if (!raw) return '—';
  const normalized = normalizeTeamCountry(raw);
  return TEAM_COUNTRY_OPTIONS.find((item) => item.code === normalized)?.label ?? raw;
}

function teamStateLabel(state?: number): string {
  if (state === 1) return 'Active';
  if (state === 0) return 'Disabled';
  return '—';
}

function displayValue(value?: string | null): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
}

export default function TeamInfoPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<SystemUserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [leaveSubmitting, setLeaveSubmitting] = useState(false);
  const [taxLabel, setTaxLabel] = useState<string | null>(null);

  useEffect(() => {
    if (!localStorage.getItem(AUTH_TOKEN_KEY)) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    fetchSystemUserInfo()
      .then((info) => {
        if (!cancelled) setProfile(info);
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const team: UserTeamMembership | undefined = getUserTeams(profile).find(
    (item) => String(item.teamId) === String(teamId ?? ''),
  );
  const isOwnTeam = isOwnCreatedUserTeam(team, profile);

  useEffect(() => {
    if (!isOwnTeam) {
      setTaxLabel(null);
      return;
    }
    let cancelled = false;
    fetchAdminTeamInfo()
      .then((info) => {
        if (!cancelled) setTaxLabel(formatInvoiceTaxLabel(info.invoice_info));
      })
      .catch(() => {
        if (!cancelled) setTaxLabel(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOwnTeam]);

  const canApplyLeave = Boolean(team) && !isOwnTeam && team?.role !== 1 && team?.isAdmin !== true;

  const closeLeaveModal = () => {
    if (leaveSubmitting) return;
    setConfirmLeave(false);
  };

  const handleConfirmLeave = async () => {
    if (!team || leaveSubmitting) return;
    setLeaveSubmitting(true);
    try {
      const result = await postApplyExitTeam(team.teamId);
      setConfirmLeave(false);
      showApiResultModal({ message: result.message, success: !result.error });
    } finally {
      setLeaveSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-green-500 text-2xl" />
          <p className="text-gray-400 text-xs mt-2">Loading team info…</p>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen bg-[#f8f9fa]">
        <SubpageHeader title="Team Info" onBack={() => navigate('/team')} />
        <div className="px-4 pt-10 text-center">
          <i className="ri-team-line text-3xl text-gray-300 mb-2 block" />
          <p className="text-sm text-gray-500 font-medium">Team not found</p>
          <p className="text-xs text-gray-400 mt-1">This team is not in your membership list.</p>
        </div>
      </div>
    );
  }

  const rows: { icon: string; label: string; value: string; multiline?: boolean }[] = [
    { icon: 'ri-phone-line', label: 'Phone', value: displayValue(team.phone) },
    { icon: 'ri-mail-line', label: 'Email', value: displayValue(team.email) },
    { icon: 'ri-map-pin-line', label: 'Address', value: displayValue(team.address), multiline: true },
    { icon: 'ri-global-line', label: 'Country', value: countryLabel(team.country) },
    ...(taxLabel ? [{ icon: 'ri-percent-line', label: 'Tax', value: taxLabel }] : []),
    { icon: 'ri-calendar-check-line', label: 'Joined', value: formatTeamDateTime(team.joinTime) },
    { icon: 'ri-checkbox-circle-line', label: 'Status', value: teamStateLabel(team.state) },
  ];

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col">
      <SubpageHeader
        title="Team Info"
        subtitle={team.teamName}
        onBack={() => navigate('/team')}
      />

      <div className={`px-4 pt-3 space-y-2.5 ${canApplyLeave ? 'pb-[calc(5.25rem+env(safe-area-inset-bottom))]' : 'pb-6'}`}>
        <div className="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-green-50 border border-green-100 overflow-hidden flex items-center justify-center flex-shrink-0">
            {team.logoPath ? (
              <img src={team.logoPath} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-green-700 text-sm font-semibold">
                {initialsFromName(team.teamName)}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-800 truncate">{team.teamName}</div>
            <div className="text-[11px] text-gray-400 mt-0.5 truncate">
              {team.teamCode || 'No team code'}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden divide-y divide-gray-100">
          {rows.map((row) => (
            <div key={row.label} className="px-3 py-2.5 flex items-start gap-2.5">
              <i className={`${row.icon} text-gray-400 text-sm mt-0.5 flex-shrink-0`} aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="text-[10px] text-gray-400 uppercase tracking-wider">{row.label}</div>
                <div
                  className={`text-xs text-gray-800 mt-0.5 ${
                    row.multiline ? 'whitespace-pre-wrap break-words' : 'truncate'
                  }`}
                >
                  {row.value}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {canApplyLeave ? (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="mx-auto w-full" style={{ maxWidth: 480 }}>
            <button
              type="button"
              onClick={() => setConfirmLeave(true)}
              className="w-full h-9 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-medium text-xs cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-1.5"
            >
              <i className="ri-logout-box-r-line text-sm" />
              Apply to Leave
            </button>
          </div>
        </div>
      ) : null}

      {confirmLeave ? (
        <>
          <div className="fixed inset-0 bg-black/40 z-[60]" onClick={closeLeaveModal} />
          <div className="fixed inset-x-5 top-1/2 -translate-y-1/2 z-[60] bg-white rounded-lg border border-gray-200 p-3 max-w-md mx-auto">
            <div className="text-gray-800 font-bold text-base mb-1">Apply to Leave Team</div>
            <p className="text-sm text-gray-600 leading-relaxed mt-2">
              Submit a request to leave {team.teamName}? The team admin will review this request.
            </p>
            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={closeLeaveModal}
                disabled={leaveSubmitting}
                className="flex-1 h-9 rounded-lg border border-gray-200 text-gray-600 text-sm font-semibold cursor-pointer disabled:opacity-60"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => void handleConfirmLeave()}
                disabled={leaveSubmitting}
                className="flex-1 h-9 rounded-lg bg-red-600 text-white text-sm font-semibold cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {leaveSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Submitting…
                  </>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
